// Lightweight DOM-based debug overlay for the Web SDK.
// Shows: SDK version, queue depth, session ID, install ID, recent events.
// Toggled via enableDebugOverlay() / disableDebugOverlay().

export interface DebugOverlayState {
  sdkVersion: string;
  queueDepth: number;
  sessionId: string;
  installId: string | null;
  appId: string;
  environment: string;
  isOnline: boolean;
  recentEvents: readonly string[];
}

export type DebugOverlayStateProvider = () => DebugOverlayState;

const OVERLAY_ID = 'layers-debug-overlay';
const OVERLAY_STYLES = `
  position: fixed;
  bottom: 8px;
  right: 8px;
  width: 360px;
  max-height: 400px;
  background: rgba(0, 0, 0, 0.88);
  color: #e0e0e0;
  font-family: monospace;
  font-size: 11px;
  line-height: 1.5;
  padding: 12px;
  border-radius: 8px;
  z-index: 999999;
  overflow-y: auto;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
`;

let overlayElement: HTMLDivElement | null = null;
let updateInterval: ReturnType<typeof setInterval> | null = null;

/**
 * Create and display the debug overlay in the DOM.
 * Updates every second with the latest state from the provider.
 */
export function enableDebugOverlay(stateProvider: DebugOverlayStateProvider): void {
  if (typeof document === 'undefined') return;

  // Don't create a duplicate
  if (overlayElement) {
    disableDebugOverlay();
  }

  overlayElement = document.createElement('div');
  overlayElement.id = OVERLAY_ID;
  overlayElement.setAttribute('style', OVERLAY_STYLES);
  document.body.appendChild(overlayElement);

  const update = (): void => {
    if (!overlayElement) return;
    const state = stateProvider();
    overlayElement.innerHTML = renderOverlay(state);
  };

  update();
  updateInterval = setInterval(update, 1000);
}

/**
 * Remove the debug overlay from the DOM and stop updates.
 */
export function disableDebugOverlay(): void {
  if (updateInterval) {
    clearInterval(updateInterval);
    updateInterval = null;
  }
  if (overlayElement && overlayElement.parentNode) {
    overlayElement.parentNode.removeChild(overlayElement);
  }
  overlayElement = null;
}

/**
 * Returns whether the debug overlay is currently active.
 */
export function isDebugOverlayActive(): boolean {
  return overlayElement !== null;
}

function renderOverlay(state: DebugOverlayState): string {
  const statusDot = state.isOnline ? '&#x1F7E2;' : '&#x1F534;';
  const lines: string[] = [
    `<div style="font-size:13px;font-weight:bold;margin-bottom:8px;">Layers Debug ${statusDot}</div>`,
    `<div><b>SDK:</b> ${escapeHtml(state.sdkVersion)}</div>`,
    `<div><b>App:</b> ${escapeHtml(state.appId)} (${escapeHtml(state.environment)})</div>`,
    `<div><b>Session:</b> ${escapeHtml(truncate(state.sessionId, 20))}</div>`,
    `<div><b>Install:</b> ${escapeHtml(truncate(state.installId ?? 'N/A', 20))}</div>`,
    `<div><b>Queue:</b> ${String(state.queueDepth)} events</div>`,
    `<div><b>Network:</b> ${state.isOnline ? 'Online' : 'Offline'}</div>`
  ];

  if (state.recentEvents.length > 0) {
    lines.push(
      `<div style="margin-top:8px;font-weight:bold;border-top:1px solid #444;padding-top:6px;">Recent Events</div>`
    );
    for (const event of state.recentEvents.slice(0, 10)) {
      lines.push(`<div style="color:#aaa;">${escapeHtml(event)}</div>`);
    }
  } else {
    lines.push(`<div style="margin-top:8px;color:#888;">No events tracked yet</div>`);
  }

  return lines.join('');
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str;
  return `${str.slice(0, maxLen)}...`;
}
